import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  addCoordinatorMyCompanyClient,
  addPublicApplicationFormCompanyClient,
  addExternalCompanyClient,
  updateExternalCompanyClient,
} from '../../../services/eventApi';

const cleanCompanyStr = (str) => String(str || '').replace(/\s*\((Company|Agency)\)\s*$/i, '').trim();

const AddClientModal = ({
  isOpen,
  onClose,
  onSuccess,
  companyId = '',
  companyName = '',
  defaultCompanyName = '',
  token = '',
  coordinatorPortal = false,
  initialData = null,
}) => {
  const initialCompany = defaultCompanyName || cleanCompanyStr(companyName) || '';
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    companyName: initialData?.companyName || initialCompany,
    brandName: initialData?.brandName || '',
    email: initialData?.email || '',
    contactNumber: initialData?.contactNumber || '',
    address: initialData?.address || '',
    coordinatorName: initialData?.coordinatorName || '',
    coordinatorEmail: initialData?.coordinatorEmail || '',
    coordinatorDesignation: initialData?.coordinatorDesignation || '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: initialData?.name || '',
        companyName: initialData?.companyName || initialCompany,
        brandName: initialData?.brandName || '',
        email: initialData?.email || '',
        contactNumber: initialData?.contactNumber || '',
        address: initialData?.address || '',
        coordinatorName: initialData?.coordinatorName || '',
        coordinatorEmail: initialData?.coordinatorEmail || '',
        coordinatorDesignation: initialData?.coordinatorDesignation || '',
      });
      setErrors({});
    }
  }, [isOpen, initialData, initialCompany]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) {
      errs.name = 'Client name is required';
    }
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Please enter a valid email address';
    }
    if (formData.coordinatorEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.coordinatorEmail.trim())) {
      errs.coordinatorEmail = 'Please enter a valid coordinator email address';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        companyName: formData.companyName.trim(),
        brandName: formData.brandName.trim(),
        email: formData.email.trim(),
        contactNumber: formData.contactNumber.trim(),
        address: formData.address.trim(),
        coordinatorName: formData.coordinatorName.trim(),
        coordinatorEmail: formData.coordinatorEmail.trim(),
        coordinatorDesignation: formData.coordinatorDesignation.trim(),
      };

      let res;
      if (initialData?._id && companyId) {
        res = await updateExternalCompanyClient(companyId, initialData._id, payload);
      } else if (coordinatorPortal) {
        res = await addCoordinatorMyCompanyClient(payload);
      } else if (token && companyId) {
        res = await addPublicApplicationFormCompanyClient(token, companyId, payload);
      } else if (companyId) {
        res = await addExternalCompanyClient(companyId, payload);
      } else {
        throw new Error('Unable to determine company to attach client to');
      }

      const client = res.data?.data || res.data;
      toast.success(initialData?._id ? 'Client updated successfully' : 'Client added successfully');
      if (onSuccess) onSuccess(client);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden border border-gray-200 animate-in fade-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {initialData?._id ? 'Edit Client' : 'Add New Client'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {companyName
                ? `${initialData?._id ? 'Update client for' : 'Adding client for'} ${companyName}`
                : initialData?._id
                ? 'Update client record'
                : 'Register a new client record'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 rounded-lg p-1 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
            {/* Primary Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Client Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g. Unilever Sri Lanka"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.name ? 'border-red-500 bg-red-50/50' : 'border-gray-300'
                  }`}
                />
                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleChange('companyName', e.target.value)}
                  placeholder="Company name"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Brand Name
                </label>
                <input
                  type="text"
                  value={formData.brandName}
                  onChange={(e) => handleChange('brandName', e.target.value)}
                  placeholder="Brand name"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Client Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="client@example.com"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.email ? 'border-red-500 bg-red-50/50' : 'border-gray-300'
                  }`}
                />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Contact Number
                </label>
                <input
                  type="text"
                  value={formData.contactNumber}
                  onChange={(e) => handleChange('contactNumber', e.target.value)}
                  placeholder="07XXXXXXXX"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Address
                </label>
                <textarea
                  rows={2}
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Client office address"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Coordinator Details Section */}
            <div className="pt-3 border-t border-gray-100">
              <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <span>Coordinator Information</span>
                <span className="text-[11px] font-normal lowercase text-gray-500">(auto-fills from company contact if blank)</span>
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                    Coordinator Name
                  </label>
                  <input
                    type="text"
                    value={formData.coordinatorName}
                    onChange={(e) => handleChange('coordinatorName', e.target.value)}
                    placeholder="Defaults to company contact person"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                    Coordinator Email
                  </label>
                  <input
                    type="email"
                    value={formData.coordinatorEmail}
                    onChange={(e) => handleChange('coordinatorEmail', e.target.value)}
                    placeholder="Defaults to company email"
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      errors.coordinatorEmail ? 'border-red-500 bg-red-50/50' : 'border-gray-300'
                    }`}
                  />
                  {errors.coordinatorEmail && <p className="mt-1 text-xs text-red-600">{errors.coordinatorEmail}</p>}
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                    Coordinator Designation
                  </label>
                  <input
                    type="text"
                    value={formData.coordinatorDesignation}
                    onChange={(e) => handleChange('coordinatorDesignation', e.target.value)}
                    placeholder="e.g. Brand Manager / Marketing Lead"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors shadow-xs disabled:opacity-50 cursor-pointer"
            >
              {loading && (
                <svg className="w-4 h-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              )}
              {loading
                ? (initialData?._id ? 'Updating...' : 'Saving...')
                : (initialData?._id ? 'Update Client' : 'Save Client')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClientModal;
